import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _classCallCheck from "@babel/runtime/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime/helpers/esm/createClass";
import _inherits from "@babel/runtime/helpers/esm/inherits";
import _createSuper from "@babel/runtime/helpers/esm/createSuper";
import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import _objectSpread from "@babel/runtime/helpers/esm/objectSpread2";
import React, { Component } from 'react';
import { compose, lifecycle } from 'recompose';
import { List, Map, mergeDeep } from 'immutable';
import { ComponentConfigContext } from '../common/ComponentConfigContext';
import { connect, dispatch } from '../../store';
import { configureTable, mountTable, unmountTable, isClientSide, filterFormKey } from './Table.redux';
import { generateKey } from '../../helpers';
import { generateForm } from '../form/Form';

var fromColumnSet = function fromColumnSet(columns, columnSet) {
  return columnSet.map(function (cs) {
    return columns.find(function (c) {
      return c.get('value') === cs;
    });
  });
};

var KeyWrapper = function KeyWrapper(_ref) {
  var children = _ref.children;
  return children;
};

var TableComponent = function TableComponent(props) {
  if (props.configured) {
    var children = props.children,
        loading = props.loading,
        initializing = props.initializing,
        rows = props.rows,
        error = props.error,
        appliedFilters = props.appliedFilters,
        components = props.components,
        tableKey = props.tableKey,
        _filterFormKey = props.filterFormKey,
        count = props.count;
    var table = buildTable(props);
    var filter = components.FilterForm ? buildFilterForm(props) : buildFilterLayout(props);
    var pagination = buildPaginationControl(props);
    return children({
      tableKey: tableKey,
      filterFormKey: _filterFormKey,
      table: table,
      filter: filter,
      appliedFilters: appliedFilters,
      pagination: pagination,
      initializing: initializing,
      loading: loading,
      rows: rows,
      count: count,
      error: error
    });
  }

  return null;
};

var filterComponentByType = function filterComponentByType(components) {
  var type = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 'text';
  return type === 'boolean' ? components.BooleanFilter : components.TextFilter;
};

var buildField = function buildField(_ref2) {
  var filter = _ref2.filter,
      filters = _ref2.filters,
      components = _ref2.components,
      columnComponents = _ref2.columnComponents,
      tableKey = _ref2.tableKey,
      tableOptions = _ref2.tableOptions,
      renderOptions = _ref2.renderOptions;
  var value = filter.get('value');
  var name = filter.getIn(['column', 'value']);
  var title = filter.getIn(['column', 'title']);
  var options = filter.getIn(['column', 'options'], function () {
    return [];
  })(tableOptions, renderOptions);

  var onChange = function onChange(value) {
    var filterName = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : name;
    dispatch('SET_FILTER', {
      tableKey: tableKey,
      filter: filterName,
      value: value
    });
  };

  var Filter = columnComponents.getIn([name, 'Filter'], filterComponentByType(components, filter.getIn(['column', 'type'])));
  return /*#__PURE__*/React.createElement(Filter, {
    value: value,
    name: name,
    title: title,
    onChange: onChange,
    options: options,
    tableOptions: tableOptions,
    filters: filters,
    visible: true
  });
};

var filtersToFields = function filtersToFields(components) {
  return Map({
    FormButtons: components.FilterFormButtons,
    FormError: components.FilterFormError,
    FormLayout: components.FilterFormLayout,
    AttributesField: components.AttributesFilter,
    CheckboxField: components.CheckboxFilter,
    CodeField: components.CodeFilter,
    FormField: components.FormFilter,
    FormMultiField: components.FormMultiFilter,
    PasswordField: components.PasswordFilter,
    RadioField: components.RadioFilter,
    TeamField: components.TeamFilter,
    TeamMultiField: components.TeamMultiFilter,
    SelectField: components.SelectFilter,
    SelectMultiField: components.SelectMultiFilter,
    TextField: components.TextFilter,
    TextMultiField: components.TextMultiFilter,
    UserField: components.UserFilter,
    UserMultiField: components.UserMultiFilter,
    TableField: components.TableFilter
  }).filter(function (c) {
    return !!c;
  });
};

var buildFilterForm = function buildFilterForm(props) {
  var FilterForm = props.components.FilterForm; // Build the form filter components.

  var components = filtersToFields(props.components);
  return /*#__PURE__*/React.createElement(FilterForm, Object.assign({}, props.tableOptions, {
    formKey: props.filterFormKey,
    tableKey: props.tableKey,
    components: components,
    alterFields: props.alterFilters,
    fieldSet: props.filterSet,
    onSave: props.onSearch,
    appliedFilters: props.appliedFilters
  }));
};

var buildFilterLayout = function buildFilterLayout(_ref3) {
  var components = _ref3.components,
      columnComponents = _ref3.columnComponents,
      filters = _ref3.filters,
      appliedFilters = _ref3.appliedFilters,
      validFilters = _ref3.validFilters,
      tableKey = _ref3.tableKey,
      columnSet = _ref3.columnSet,
      loading = _ref3.loading,
      initializing = _ref3.initializing,
      tableOptions = _ref3.tableOptions,
      renderOptions = _ref3.renderOptions;
  // Add an onChange to each filter and convert it to a list for looping.
  var f = filters.map(function (filter) {
    return buildField({
      appliedFilters: appliedFilters,
      filter: filter,
      filters: filters,
      components: components,
      columnComponents: columnComponents,
      tableKey: tableKey,
      tableOptions: tableOptions,
      renderOptions: renderOptions
    });
  });

  var onSearch = function onSearch(e) {
    if (e) {
      e.preventDefault();
    }

    dispatch('APPLY_FILTERS', {
      tableKey: tableKey
    });
  };

  var onReset = function onReset(e) {
    e.preventDefault();
    dispatch('CLEAR_TABLE_FILTERS', {
      tableKey: tableKey
    });
    dispatch('APPLY_FILTERS', {
      tableKey: tableKey
    });
  };

  var onClear = function onClear(e) {
    e.preventDefault();
    dispatch('CLEAR_SELECTED_FILTERS', {
      tableKey: tableKey
    });
  };

  var onChangeFilter = function onChangeFilter(value, filterName) {
    dispatch('SET_FILTER', {
      tableKey: tableKey,
      filter: filterName,
      value: value
    });
  };

  var FilterLayout = components.FilterLayout;
  return /*#__PURE__*/React.createElement(FilterLayout, {
    filters: f,
    appliedFilters: appliedFilters,
    validFilters: validFilters,
    onSearch: onSearch,
    onReset: onReset,
    onClear: onClear,
    onChangeFilter: onChangeFilter,
    columnSet: columnSet,
    loading: loading,
    initializing: initializing,
    tableOptions: tableOptions,
    renderOptions: renderOptions,
    tableKey: tableKey
  });
};

var hasPrevPage = function hasPrevPage(data, dataSource, tableOptions, pageTokens, pageOffset) {
  return isClientSide(Map({
    data: data,
    dataSource: dataSource,
    tableOptions: tableOptions
  })) ? pageOffset > 0 : pageTokens.size !== 0;
};

var hasNextPage = function hasNextPage(data, dataSource, tableOptions, pageOffset, pageSize, currentPageToken, rows) {
  return isClientSide(Map({
    data: data,
    dataSource: dataSource,
    tableOptions: tableOptions
  })) ? rows.size === pageSize ? data ? pageOffset + pageSize < data.size : false : !!currentPageToken : !!currentPageToken;
};

var getIndexes = function getIndexes(data, rows, dataSource, tableOptions, pageOffset, pageSize, pageTokens) {
  return isClientSide(Map({
    data: data,
    dataSource: dataSource,
    tableOptions: tableOptions
  })) ? {
    startIndex: data.size > 0 ? pageOffset + 1 : 0,
    endIndex: data.size > 0 ? pageOffset + rows.size : 0
  } : {
    startIndex: pageTokens.size * pageSize + (rows.size > 0 ? 1 : 0),
    endIndex: pageTokens.size * pageSize + rows.size
  };
};

var buildPaginationControl = function buildPaginationControl(props) {
  var tableKey = props.tableKey,
      filterFormKey = props.filterFormKey,
      data = props.data,
      rows = props.rows,
      dataSource = props.dataSource,
      tableOptions = props.tableOptions,
      pageTokens = props.pageTokens,
      pageSize = props.pageSize,
      pageOffset = props.pageOffset,
      currentPageToken = props.currentPageToken,
      count = props.count,
      components = props.components,
      loading = props.loading;
  var PaginationControl = components.PaginationControl;
  var prevPage = hasPrevPage(data, dataSource, tableOptions, pageTokens, pageOffset) ? onPrevPage(tableKey) : null;
  var nextPage = hasNextPage(data, dataSource, tableOptions, pageOffset, pageSize, currentPageToken, rows) ? onNextPage(tableKey) : null;

  var _getIndexes = getIndexes(data, rows, dataSource, tableOptions, pageOffset, pageSize, pageTokens),
      startIndex = _getIndexes.startIndex,
      endIndex = _getIndexes.endIndex;

  return /*#__PURE__*/React.createElement(PaginationControl, {
    tableKey: tableKey,
    filterFormKey: filterFormKey,
    prevPage: prevPage,
    nextPage: nextPage,
    gotoPage: onGotoPage(tableKey),
    loading: loading,
    startIndex: startIndex,
    endIndex: endIndex,
    count: count
  });
};

export var buildTable = function buildTable(props) {
  var TableLayout = props.components.TableLayout;
  var header = buildTableHeader(props);
  var body = buildTableBody(props);
  var footer = buildTableFooter(props);
  return /*#__PURE__*/React.createElement(TableLayout, {
    tableKey: props.tableKey,
    filterFormKey: props.filterFormKey,
    header: header,
    body: body,
    footer: footer,
    initializing: props.initializing,
    loading: props.loading,
    error: props.error,
    empty: props.rows.isEmpty(),
    renderOptions: props.renderOptions,
    tableOptions: props.tableOptions
  });
};
export var buildTableHeader = function buildTableHeader(props) {
  var Header = props.components.Header;
  var headerRow = buildTableHeaderRow(props);
  return props.omitHeader ? null : /*#__PURE__*/React.createElement(Header, {
    sortable: props.sortable,
    headerRow: headerRow,
    renderOptions: props.renderOptions,
    rows: props.rows,
    tableOptions: props.tableOptions
  });
};
export var buildTableHeaderRow = function buildTableHeaderRow(props) {
  var components = props.components,
      rows = props.rows,
      columns = props.columns,
      columnSet = props.columnSet;
  var HeaderRow = components.HeaderRow;
  var columnHeaders = fromColumnSet(columns, columnSet).map(buildTableHeaderCell(props));
  return /*#__PURE__*/React.createElement(HeaderRow, {
    columnHeaders: columnHeaders,
    renderOptions: props.renderOptions,
    rows: rows,
    tableOptions: props.tableOptions
  });
};
export var buildTableHeaderCell = function buildTableHeaderCell(props) {
  return function (column, index) {
    var tableKey = props.tableKey,
        components = props.components,
        columnComponents = props.columnComponents,
        renderOptions = props.renderOptions,
        tableOptions = props.tableOptions;
    var HeaderCell = columnComponents.getIn([column.get('value'), 'HeaderCell'], components.HeaderCell);
    var sorting = column === props.sortColumn && props.sortDirection;
    var sortable = props.sortable && column.get('sortable');
    return /*#__PURE__*/React.createElement(KeyWrapper, {
      key: "column-".concat(index)
    }, /*#__PURE__*/React.createElement(HeaderCell, {
      onSortColumn: onSortColumn(tableKey, column),
      title: column.get('title'),
      renderOptions: renderOptions,
      sorting: sorting,
      sortable: sortable,
      tableOptions: tableOptions
    }));
  };
};
export var buildTableBody = function buildTableBody(props) {
  var Body = props.components.Body;
  var tableRows = buildTableBodyRows(props);
  return /*#__PURE__*/React.createElement(Body, {
    loading: props.loading,
    initializing: props.initializing,
    renderOptions: props.renderOptions,
    tableOptions: props.tableOptions,
    tableRows: tableRows,
    error: props.error,
    empty: props.rows.isEmpty()
  });
};
export var buildTableBodyRows = function buildTableBodyRows(props) {
  var components = props.components,
      rows = props.rows,
      columns = props.columns,
      columnSet = props.columnSet,
      appliedFilters = props.appliedFilters,
      renderOptions = props.renderOptions,
      tableOptions = props.tableOptions;
  var BodyRow = components.BodyRow;
  var EmptyBodyRow = components.EmptyBodyRow;
  var tableRows = rows.size > 0 ? rows.map(function (row, index) {
    var cells = buildTableBodyCells(props, row, index);
    return /*#__PURE__*/React.createElement(KeyWrapper, {
      key: "row-".concat(index)
    }, /*#__PURE__*/React.createElement(BodyRow, {
      cells: cells,
      columns: columns,
      row: row,
      index: index,
      renderOptions: renderOptions,
      tableOptions: tableOptions
    }));
  }) : /*#__PURE__*/React.createElement(EmptyBodyRow, {
    colSpan: columnSet.size,
    initializing: props.initializing,
    loading: props.loading,
    appliedFilters: appliedFilters,
    renderOptions: renderOptions,
    tableOptions: tableOptions,
    error: props.error
  });
  return tableRows;
};
export var buildTableBodyCells = function buildTableBodyCells(props, row, rowIndex) {
  var tableKey = props.tableKey,
      components = props.components,
      rows = props.rows,
      columns = props.columns,
      columnSet = props.columnSet,
      columnComponents = props.columnComponents,
      renderOptions = props.renderOptions,
      tableOptions = props.tableOptions;
  return fromColumnSet(columns, columnSet).map(function (column, index) {
    var BodyCell = columnComponents.getIn([column.get('value'), 'BodyCell'], components.BodyCell);
    var value = row.get(column.get('value'));
    return /*#__PURE__*/React.createElement(KeyWrapper, {
      key: "column-".concat(index)
    }, /*#__PURE__*/React.createElement(BodyCell, {
      tableKey: tableKey,
      row: row,
      rowIndex: rowIndex,
      index: index,
      value: value,
      rows: rows,
      column: column,
      renderOptions: renderOptions,
      tableOptions: tableOptions
    }));
  });
};
export var buildTableFooter = function buildTableFooter(props) {
  var components = props.components,
      columnSet = props.columnSet,
      renderOptions = props.renderOptions,
      rows = props.rows,
      tableOptions = props.tableOptions,
      includeFooter = props.includeFooter;
  var footerRow = buildTableFooterRow(props);
  var Footer = components.Footer;
  return includeFooter ? /*#__PURE__*/React.createElement(Footer, {
    rows: rows,
    footerRow: footerRow,
    colSpan: columnSet.size,
    renderOptions: renderOptions,
    tableOptions: tableOptions
  }) : null;
};
export var buildTableFooterRow = function buildTableFooterRow(props) {
  var components = props.components,
      renderOptions = props.renderOptions,
      tableOptions = props.tableOptions;
  var cells = buildTableFooterCells(props);
  var FooterRow = components.FooterRow;
  return /*#__PURE__*/React.createElement(FooterRow, {
    cells: cells,
    renderOptions: renderOptions,
    tableOptions: tableOptions
  });
};
export var buildTableFooterCells = function buildTableFooterCells(props) {
  var components = props.components,
      columns = props.columns,
      columnSet = props.columnSet,
      columnComponents = props.columnComponents,
      renderOptions = props.renderOptions,
      tableOptions = props.tableOptions;
  return fromColumnSet(columns, columnSet).map(function (column, index) {
    var FooterCell = columnComponents.getIn([column.get('value'), 'FooterCell'], components.FooterCell);
    return /*#__PURE__*/React.createElement(KeyWrapper, {
      key: "column-".concat(index)
    }, /*#__PURE__*/React.createElement(FooterCell, {
      column: column,
      renderOptions: renderOptions,
      tableOptions: tableOptions
    }));
  });
};

var onNextPage = function onNextPage(tableKey) {
  return function () {
    return dispatch('NEXT_PAGE', {
      tableKey: tableKey
    });
  };
};

var onPrevPage = function onPrevPage(tableKey) {
  return function () {
    return dispatch('PREV_PAGE', {
      tableKey: tableKey
    });
  };
};

var onGotoPage = function onGotoPage(tableKey) {
  return function (pageNumber) {
    return function () {
      return dispatch('GOTO_PAGE', {
        tableKey: tableKey,
        pageNumber: pageNumber
      });
    };
  };
};

var onSortColumn = function onSortColumn(tableKey, column) {
  return function () {
    return dispatch('SORT_COLUMN', {
      tableKey: tableKey,
      column: column
    });
  };
};

var mapStateToProps = function mapStateToProps(state) {
  return function (state, props) {
    return state.getIn(['tables', props.tableKey], Map()).toObject();
  };
};
/**
 * @component
 */


var TableImpl = compose(connect(mapStateToProps), lifecycle({
  componentDidMount: function componentDidMount() {
    if (this.props.mounted && !this.props.configured) {
      configureTable(this.props);
    }
  },
  componentDidUpdate: function componentDidUpdate() {
    if (this.props.mounted && !this.props.configured) {
      configureTable(this.props);
    }
  }
}))(TableComponent);
export var generateColumns = function generateColumns(columns) {
  var addColumns = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  var alterColumns = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  return List(addColumns).concat(columns).map(function (c) {
    return Map(_objectSpread(_objectSpread(_objectSpread({}, c), alterColumns[c.value]), {}, {
      value: c.value
    }));
  });
};
export var extractColumnComponents = function extractColumnComponents(columns) {
  return columns.filter(function (c) {
    return c.has('components');
  }).reduce(function (result, current) {
    return result.set(current.get('value'), current.get('components'));
  }, Map());
};
export var generateTable = function generateTable(_ref4) {
  var _ref4$tableOptions = _ref4.tableOptions,
      tableOptions = _ref4$tableOptions === void 0 ? [] : _ref4$tableOptions,
      _ref4$filterDataSourc = _ref4.filterDataSources,
      filterDataSources = _ref4$filterDataSourc === void 0 ? function () {
    return {};
  } : _ref4$filterDataSourc,
      filters = _ref4.filters,
      columns = _ref4.columns,
      dataSource = _ref4.dataSource,
      sortable = _ref4.sortable,
      onValidateFilters = _ref4.onValidateFilters;
  return function (props) {
    var tableOptionProps = tableOptions ? tableOptions.reduce(function (to, opt) {
      to[opt] = props[opt];
      return to;
    }, {}) : {};
    var FilterForm;

    if (filters) {
      FilterForm = generateForm({
        dataSources: filterDataSources,
        fields: filters,
        formOptions: ['tableKey'].concat(_toConsumableArray(tableOptions)),
        handleSubmit: function handleSubmit(_ref5) {
          var tableKey = _ref5.tableKey;
          return function (values) {
            dispatch('APPLY_FILTER_FORM', {
              tableKey: tableKey,
              appliedFilters: values
            });
            return {
              values: values
            };
          };
        }
      });
    }

    var setProps = {
      // Passed in to `generateTable`
      columns: columns,
      dataSource: dataSource,
      onValidateFilters: onValidateFilters,
      // Calculated from props and tableOptions.
      tableOptions: _objectSpread({}, tableOptionProps),
      // Add FilterForm to the components that are passed.
      components: _objectSpread(_objectSpread({}, props.components), {}, {
        FilterForm: FilterForm
      }),
      // Sortable can be enabled or disabled for an entire table.
      sortable: typeof sortable !== 'undefined' ? sortable : props.sortable,
      // Explicitly allowed props.
      tableKey: props.tableKey,
      filterFormKey: filterFormKey(props.tableKey),
      addColumns: props.addColumns,
      alterColumns: props.alterColumns,
      alterFilters: mergeDeep(props.alterFilters || {}, Map(props.initialFilterValues).map(function (initialValue) {
        return {
          initialValue: initialValue
        };
      }).toObject()),
      filterSet: props.filterSet,
      columnSet: props.columnSet,
      pageSize: props.pageSize,
      defaultSortColumn: props.defaultSortColumn,
      defaultSortDirection: props.defaultSortDirection,
      omitHeader: props.omitHeader,
      includeFooter: props.includeFooter,
      renderOptions: props.renderOptions,
      uncontrolled: props.uncontrolled,
      // For full client-side tables, with no datasource.
      data: props.data,
      filterForm: !!filters,
      initialFilterValues: props.initialFilterValues || {},
      onSearch: props.onSearch,
      onFetch: props.onFetch
    };
    return /*#__PURE__*/React.createElement(Table, setProps, props.children);
  };
};
export var Table = /*#__PURE__*/function (_Component) {
  _inherits(Table, _Component);

  var _super = _createSuper(Table);

  function Table(props) {
    var _this;

    _classCallCheck(this, Table);

    _this = _super.call(this, props);

    var components = props.components,
        tableKey = props.tableKey,
        config = _objectWithoutProperties(props, ["components", "tableKey"]);

    _this.components = components;
    _this.config = config;
    _this.auto = !tableKey || props.uncontrolled;
    _this.tableKey = tableKey || generateKey();
    return _this;
  }

  _createClass(Table, [{
    key: "componentDidMount",
    value: function componentDidMount() {
      if (this.auto) {
        mountTable(this.tableKey);
      }
    }
  }, {
    key: "componentWillUnmount",
    value: function componentWillUnmount() {
      if (this.auto) {
        unmountTable(this.tableKey);
      }
    }
  }, {
    key: "render",
    value: function render() {
      var _this2 = this;

      var columns = generateColumns(this.props.columns, this.props.addColumns, this.props.alterColumns);
      var allColumns = columns.map(function (c) {
        return c.get('value');
      });
      var columnSet = List(this.props.columnSet ? typeof this.props.columnSet === 'function' ? this.props.columnSet(allColumns) : this.props.columnSet : allColumns).filter(function (c) {
        return columns.find(function (c2) {
          return c2.get('value') === c;
        });
      });
      var columnComponents = extractColumnComponents(columns);
      return /*#__PURE__*/React.createElement(ComponentConfigContext.Consumer, null, function (componentConfig) {
        return /*#__PURE__*/React.createElement(TableImpl, Object.assign({}, _this2.props, {
          components: componentConfig.merge(_this2.props.components).toJS(),
          columnComponents: columnComponents,
          columns: columns,
          columnSet: columnSet,
          tableKey: _this2.tableKey,
          auto: _this2.auto
        }), _this2.props.children);
      });
    }
  }]);

  return Table;
}(Component);
var defaultProps = {
  components: {},
  rows: List([]),
  columns: [],
  addColumns: [],
  alterColumns: {},
  sortable: true,
  pageSize: 25,
  omitHeader: false,
  includeFooter: false
};
Table.defaultProps = defaultProps;
Table.configure = configureTable;